import { useTranslation } from 'react-i18next';
import { Button } from '../../Fundamentals/Button';
import EmptyState from '../../Fundamentals/EmptyState/EmptyState';
import SpirisIcon from '../../Fundamentals/Icon/SpirisIcon';
import RowActionsMenu from '../../Fundamentals/RowActionsMenu/RowActionsMenu';
import PackageStatusBadge from './PackageStatusBadge';
import dateUtils from '../../../Utils/dateUtils';
import { salesChannelStatus, salesChannelStatusConfig } from './packageConstants';
import type { PackageSalesChannel, SalesChannel } from './Packages.types';

interface PackageSalesChannelsTabProps {
    salesChannels: PackageSalesChannel[] | null;
    catalog: SalesChannel[];
    onAdd: () => void;
    onEdit: (channel: PackageSalesChannel) => void;
    onRemove: (channel: PackageSalesChannel) => void;
}

const PackageSalesChannelsTab = ({ salesChannels, catalog, onAdd, onEdit, onRemove }: PackageSalesChannelsTabProps) => {
    const { t } = useTranslation();

    // the link model carries no code, so it comes from the catalog entry with the same id
    const codeById = new Map(catalog.map((channel) => [channel.id, channel.clientId ?? '']));

    const renderList = () => {
        if (salesChannels === null)
            return (
                <div className="alert alert-warning alert-sm" role="alert">
                    {t('packages.salesChannelsNotLoaded')}
                </div>
            );

        if (salesChannels.length === 0) return <EmptyState icon="users" size="sm" title={t('packages.noSalesChannels')} />;

        return (
            <table className="table table-stripe skynet-table-no-divider">
                <thead>
                    <tr>
                        <th className="col-4">{t('packages.channel')}</th>
                        <th className="col-2">{t('packages.code')}</th>
                        <th className="col-2">{t('packages.validFrom')}</th>
                        <th className="col-2">{t('packages.validTo')}</th>
                        <th className="col-1">{t('common.status')}</th>
                        <th className="col-1" />
                    </tr>
                </thead>
                <tbody>
                    {salesChannels.map((channel) => {
                        const status = salesChannelStatusConfig[salesChannelStatus(channel.validFrom, channel.validTo)];
                        const description = channel.description ?? '';

                        return (
                            <tr key={channel.id}>
                                <td className="py-12">
                                    <div className="d-flex align-items-center gap-12">
                                        <SpirisIcon name="users" />
                                        <div>
                                            <div className="font-weight-600">{channel.name}</div>
                                            {description.length > 0 && <div className="text-secondary font-size-xs mt-4">{description}</div>}
                                        </div>
                                    </div>
                                </td>
                                <td className="py-12">
                                    <code>{codeById.get(channel.id) ?? ''}</code>
                                </td>
                                <td className="py-12">
                                    <code>{dateUtils.formatDateOrPlaceholder(channel.validFrom, '—')}</code>
                                </td>
                                <td className="py-12">
                                    {channel.validTo === null || channel.validTo === undefined ? (
                                        <code className="text-secondary">{t('packages.untilFurtherNotice')}</code>
                                    ) : (
                                        <code>{dateUtils.formatDateOrPlaceholder(channel.validTo, '—')}</code>
                                    )}
                                </td>
                                <td className="py-12">
                                    <PackageStatusBadge status={status.badgeStatus} label={t(status.labelKey) ?? ''} />
                                </td>
                                <td className="py-12 text-right">
                                    <RowActionsMenu
                                        label={t('packages.rowActions') ?? ''}
                                        actions={[
                                            { id: 'edit', label: t('packages.editValidity'), icon: 'edit', onClick: () => onEdit(channel) },
                                            { id: 'remove', label: t('packages.removeSalesChannelLink'), icon: 'delete', destructive: true, onClick: () => onRemove(channel) },
                                        ]}
                                    />
                                </td>
                            </tr>
                        );
                    })}
                </tbody>
            </table>
        );
    };

    return (
        <>
            <div className="d-flex justify-content-between align-items-start gap-16 mb-16">
                <div>
                    <h2 className="font-20 mb-4">{t('packages.salesChannels')}</h2>
                    <p className="text-secondary mb-0">{t('packages.salesChannelsDescription')}</p>
                </div>
                {/* without the linked list the picker can't hide channels already linked, so adding would risk duplicates */}
                {Array.isArray(salesChannels) && (
                    <Button className="flex-shrink-0" onClick={onAdd}>
                        <SpirisIcon name="plus" className="mr-8" />
                        {t('packages.addSalesChannel')}
                    </Button>
                )}
            </div>
            <div className="alert alert-info alert-sm mw-100 min-h-0 lh-xl mb-16">{t('packages.salesChannelsPackageScoped')}</div>
            {renderList()}
        </>
    );
};

export default PackageSalesChannelsTab;
